import React, { useState, useRef, useMemo } from 'react';
import { ShareNetwork, DownloadSimple, Copy, Check, InstagramLogo, TiktokLogo, FacebookLogo, MusicNotes } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MOODS = [
  { id: 'pop',       label: 'Upbeat Pop',  desc: '124 BPM · dance-floor energy' },
  { id: 'chill',     label: 'Chill Lo-fi', desc: '92 BPM · warm & nostalgic' },
  { id: 'cinematic', label: 'Cinematic',   desc: '80 BPM · orchestral & epic' },
];

// Per-event promo caption — mixes the event name in so each host's post feels native.
const buildCaption = (eventName) => {
  const hostedEvent = (eventName || 'my event').trim();
  return `Just captured every moment from ${hostedEvent} with @eventdrop 📸

One QR code. Every photo, video & message — from every guest — in one gallery.

Book yours → eventdrop.co
Capture £39 · Experience £129 · Legacy £199

#EventDrop #EventPhotography #QRCode #PartyReel`;
};

const ShareKit = ({ event }) => {
  const eventName = event?.event_name || event?.name || 'my event';
  const [caption, setCaption] = useState(() => buildCaption(eventName));
  const [captionCopied, setCaptionCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mood, setMood] = useState('pop');
  const videoRef = useRef(null);

  // URLs rebuild with each mood change so the <video> element reloads.
  const videoUrl = useMemo(() => `${window.location.origin}/demo-${mood}-vertical.mp4`, [mood]);
  const pageUrl = 'https://eventdrop.co';

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCaptionCopied(true);
      toast.success('Caption copied');
      setTimeout(() => setCaptionCopied(false), 1800);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const downloadVideo = async () => {
    setBusy(true);
    try {
      // Force download via blob so mobile Safari saves it instead of autoplaying in a new tab.
      const res = await fetch(videoUrl);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `eventdrop-reel-${(eventName || 'event').replace(/\s+/g, '-').toLowerCase()}-${mood}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      toast.success('Reel saved to your device');
    } catch (e) {
      toast.error('Download failed — try again');
    } finally {
      setBusy(false);
    }
  };

  // Native share-sheet (best on mobile — lets user pick IG/TikTok/WhatsApp/etc directly).
  const nativeShare = async () => {
    if (!navigator.share) {
      toast.info('Use the platform buttons below on desktop');
      return;
    }
    try {
      let files = undefined;
      // Try including the mp4 so IG/TikTok can import it directly; fall back to link-only.
      try {
        const res = await fetch(videoUrl);
        const blob = await res.blob();
        const file = new File([blob], 'eventdrop-reel.mp4', { type: 'video/mp4' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          files = [file];
        }
      } catch {}

      await navigator.share({
        title: `${eventName} — captured by EventDrop`,
        text: caption,
        url: pageUrl,
        ...(files ? { files } : {}),
      });
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Share cancelled');
    }
  };

  // Platform-specific shortcuts — best-effort deep links with web fallbacks.
  // IG + TikTok mobile apps don't accept arbitrary URL imports, so we copy the caption
  // automatically and open the upload screen; user pastes + attaches the saved reel.
  const shareInstagram = async () => {
    await copyCaption();
    // On iOS: instagram:// opens app; on Android: intent://; fallback to https
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) {
      window.location.href = 'https://www.instagram.com/';
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      window.location.href = 'instagram://story-camera';
      setTimeout(() => { window.location.href = 'https://www.instagram.com/'; }, 1200);
    } else {
      window.open('https://www.instagram.com/', '_blank');
    }
    toast.info('Caption copied — paste it after attaching the saved reel');
  };

  const shareTikTok = async () => {
    await copyCaption();
    const ua = navigator.userAgent || '';
    if (/android|iphone|ipad|ipod/i.test(ua)) {
      // TikTok deep link for upload
      window.location.href = 'snssdk1233://upload';
      setTimeout(() => { window.location.href = 'https://www.tiktok.com/upload'; }, 1200);
    } else {
      window.open('https://www.tiktok.com/upload', '_blank');
    }
    toast.info('Caption copied — attach the saved reel, then paste');
  };

  const shareFacebookStories = () => {
    // FB Stories open API requires app registration; simplest portable approach is the share URL.
    const url = encodeURIComponent(pageUrl);
    const quote = encodeURIComponent(caption);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank', 'width=600,height=600');
  };

  return (
    <div
      className="mb-8 rounded-2xl p-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(184,151,98,0.12), rgba(194,157,82,0.08))',
        border: '1px solid rgba(184,151,98,0.25)',
      }}
      data-testid="share-kit"
    >
      <div className="flex items-start gap-4 mb-5">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        >
          <ShareNetwork size={22} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-white text-lg">Share Your Event Recap</h4>
          <p className="text-sm text-gray-300 mt-0.5">
            One-tap post for Instagram, TikTok, and Stories — caption pre-written, reel ready to attach.
          </p>
        </div>
      </div>

      {/* Mood picker — swaps soundtrack on the 9:16 reel */}
      <div className="mb-5" data-testid="share-kit-mood-picker">
        <div className="flex items-center gap-2 mb-2">
          <MusicNotes size={14} weight="bold" className="text-gray-400" />
          <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Soundtrack mood</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => {
            const active = m.id === mood;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMood(m.id)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold text-left transition-all border ${
                  active
                    ? 'bg-white/10 border-white/40 text-white scale-[1.02]'
                    : 'bg-white/0 border-white/10 text-gray-400 hover:border-white/20 hover:text-white'
                }`}
                data-testid={`share-kit-mood-${m.id}`}
                aria-pressed={active}
              >
                <div className="leading-tight">{m.label}</div>
                <div className="text-[10px] font-normal opacity-70 mt-0.5">{m.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5 items-start">
        {/* Vertical preview */}
        <div className="rounded-xl overflow-hidden border border-white/10 bg-black mx-auto md:mx-0" style={{ width: 180, aspectRatio: '9/16' }}>
          <video
            ref={videoRef}
            key={videoUrl}
            src={videoUrl}
            poster={`${window.location.origin}/demo-vertical-poster.jpg`}
            className="w-full h-full object-cover"
            playsInline
            muted
            loop
            autoPlay
            data-testid="share-kit-preview"
          />
        </div>

        {/* Caption + actions */}
        <div className="flex flex-col gap-3">
          <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Caption</label>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={6}
            className="w-full rounded-xl bg-[#15161B] border border-white/10 px-3 py-2 text-sm text-gray-200 resize-y focus:outline-none focus:border-white/30"
            data-testid="share-kit-caption"
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={copyCaption}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
              data-testid="share-kit-copy-caption"
            >
              {captionCopied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
              {captionCopied ? 'Copied' : 'Copy caption'}
            </button>
            <button
              onClick={downloadVideo}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors disabled:opacity-50"
              data-testid="share-kit-download"
            >
              <DownloadSimple size={16} weight="bold" />
              {busy ? 'Preparing…' : 'Download reel (9:16)'}
            </button>
            <button
              onClick={nativeShare}
              className="inline-flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="share-kit-native-share"
            >
              <ShareNetwork size={16} weight="bold" />
              Share…
            </button>
          </div>

          <div className="mt-1 flex flex-wrap gap-2">
            <button
              onClick={shareInstagram}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-semibold transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #E1306C, #FCAF45)', color: '#fff' }}
              data-testid="share-kit-instagram"
            >
              <InstagramLogo size={14} weight="bold" />
              Instagram
            </button>
            <button
              onClick={shareTikTok}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-semibold bg-black hover:bg-neutral-900 border border-white/20 text-white transition-colors"
              data-testid="share-kit-tiktok"
            >
              <TiktokLogo size={14} weight="bold" />
              TikTok
            </button>
            <button
              onClick={shareFacebookStories}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-semibold transition-colors"
              style={{ background: '#1877F2', color: '#fff' }}
              data-testid="share-kit-facebook"
            >
              <FacebookLogo size={14} weight="bold" />
              Facebook
            </button>
          </div>

          <p className="text-[11px] text-gray-500 mt-1">
            Tip — hit <span className="text-gray-300 font-semibold">Share…</span> on mobile to open the native share sheet; on desktop, download the reel first, then open your platform of choice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareKit;
